import { useCallback, useEffect, useMemo, useState } from 'react';

import { isCarouselOffer } from '../../../modules/offers/offer.type-guards';
import { useOffersCarouselCategories } from '../features/offers-carousel/use-offers-carousel-categories.hook';
import { useRudderStack } from '../../../lib/rudderStack/useRudderStack';
import { Offer } from '../../../modules/offers/offer.types';

export const useOfferCategories = (offers: Offer[]) => {
  const { triggerEvent } = useRudderStack();

  const carouselOffers = useMemo(() => offers.filter(isCarouselOffer), [offers]);
  const categories = useOffersCarouselCategories(carouselOffers);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const selectedCategory = useMemo(
    () => categories.find(({ id }) => id === selectedCategoryId) || null,
    [categories, selectedCategoryId],
  );

  const handleCategoryChanged = useCallback(
    (nextCategoryId: string) => {
      const nextCategory = categories.find(({ id }) => id === nextCategoryId);
      if (!nextCategory || nextCategoryId === selectedCategoryId) {
        return;
      }

      triggerEvent('home_categories', { category: nextCategory.title });
      setSelectedCategoryId(nextCategoryId);
    },
    [categories, selectedCategoryId, triggerEvent],
  );

  useEffect(() => {
    if (selectedCategory || !categories[0]) {
      return;
    }
    handleCategoryChanged(categories[0].id);
  }, [selectedCategory, handleCategoryChanged, categories]);

  return useMemo(() => {
    return {
      categories,
      selectedCategory,
      onCategoryChange: handleCategoryChanged,
    };
  }, [categories, handleCategoryChanged, selectedCategory]);
};
